// src/lib/statsCompute.js — the Stats tab's attendance walker and chart-series builders.
//
// Extracted from StatsTab.jsx on 2026-09-06 so they can be tested under `node --test`. Inside the
// component they were unreachable: importing StatsTab drags in recharts and the Supabase client.
// Every regression found in the tab that day (meetings from a frozen table, past days judged
// against today's schedule) was in THIS code, and none of it had a test. Nothing here touches the
// DOM or the network; the only impurity is the display locale for bucket labels.
import { slotsOnDate, timeToMinutes } from './schedule.js'
import { toStr, today } from './statsRange.js'
import { diffMinutes } from './attendance.js'
import { getLanguage } from './i18n.js'
// As-of office hours (scheduler 0026). ./spells.js imports nothing, so no cycle is possible.
import { officeHoursAt } from './spells.js'

const dateLocale = () => getLanguage() === 'ka' ? 'ka-GE' : 'en-GB'

export function monthKey(dateStr) {
  return dateStr.slice(0, 7) // "2026-06"
}

export function monthLabel(key) {
  const [y, m] = key.split('-')
  return new Date(+y, +m - 1, 1).toLocaleDateString(dateLocale(), { month: 'short', year: '2-digit' })
}

// Minutes scheduled for an agent on a date, from the schedule in force that day (see
// slotsOnDate). Works for past dates whether or not a history snapshot exists.
export function scheduledMinsFromSchedule(agent, date, schedHist = []) {
  return slotsOnDate(agent, date, schedHist).reduce((sum, s) => {
    const m = timeToMinutes(s.end) - timeToMinutes(s.start)
    return sum + (m > 0 ? m : 0)
  }, 0)
}

// 🔴 A SPLIT SHIFT IS STILL ONE DAY'S WORK (2026-09-07). Both callers of this read
// `rec.slots[0]` only, while scheduledMinsFromSchedule above SUMS every slot — so
// 10:00–13:00 plus 14:00–18:00 reported 3 hours worked against 7 scheduled and handed the
// agent a 4-hour deficit they had not incurred. The two halves of one subtraction were
// counting different things.
//
// ⚠️ SLOTS WIN over the top-level pair when they exist. A top-level 10:00→18:00 spanning a
// split day bills the unpaid gap between the shifts as worked time; the pair is the fallback
// for single-slot days, which is every day with no slots array.
//
// ⚠️ It is NOT the read the lateness check wants. Being late is about the FIRST arrival and
// has nothing to say about the afternoon, so that check keeps slots[0] deliberately — the two
// questions look alike and are not, which is why this is a named function and not an inline
// expression copied to both sites.
export function workedMinutesOf(rec, diff) {
  const pairs = Array.isArray(rec?.slots)
    ? rec.slots.filter(sl => sl?.check_in && sl?.check_out)
    : []
  if (pairs.length) {
    return pairs.reduce((sum, sl) => {
      const m = diff(sl.check_in, sl.check_out)
      return sum + (m > 0 ? m : 0)
    }, 0)
  }
  if (rec?.check_in && rec?.check_out) return Math.max(diff(rec.check_in, rec.check_out), 0)
  return 0
}

// ─── The office's standard day (owner ruling 2026-09-10) ──────────────────────
// "lets say we have nothing to calculate on, we take the days he has done anything at all and
// assume a full working day as the office's working day."
//
// The numbers come from Admin → Offices (`offices.work_start`/`work_end`, and
// `weekend_work_start`/`weekend_work_end` for Sat–Sun) — the same row that already supplies the
// default slot when a weekend day is toggled on. Nothing new is configured, deliberately: a
// second place to state "how long is a working day" is a second answer waiting to disagree with
// the first, and the late threshold already taught this codebase what an unconfigured key costs
// (no row at all, and every consumer falling back to its own literal 15).
//
// ⚠️ Returns null when the office is unknown or its hours are unreadable, and the caller must
// then credit NOTHING. An invented day length is worse than a blank cell: a blank says "we do
// not know", a wrong one says "he worked eight hours" about a day nobody measured.
// ⚠️ THE HOURS ARE AS-OF `date`, NOT THE OFFICE'S CURRENT ROW (scheduler 0026, owner ruling
// 2026-09-20: "we need a date stamp on every place"). This function has always TAKEN the date
// and, until that day, used it only to pick weekday-vs-weekend while reading the hours off the
// live row — so editing an office's hours in Admin silently re-measured every past day already
// credited, and the lateness judged against the start time with it. `hours` is the dated spell
// list from getOfficeHistory(); with no spell covering the day it falls back to the office row,
// which is exactly the pre-0026 answer, so nothing that works today changes.
export function officeDayMinutes(office, date, hours = []) {
  if (!office) return null
  const weekend = [0, 6].includes(new Date(date + 'T12:00:00').getDay())
  // The spell is the unit: when one covers this day, ALL FOUR times come from it.
  const asOf = officeHoursAt(office.id, date, hours) || office
  const start = weekend ? asOf.weekend_work_start : asOf.work_start
  const end   = weekend ? asOf.weekend_work_end   : asOf.work_end
  if (!start || !end) return null
  const m = timeToMinutes(end) - timeToMinutes(start)
  return m > 0 ? m : null
}

// ─── Per-agent stats ──────────────────────────────────────────────────────────
// Walks every calendar day in [from, to] (capped at today) and uses the agent's
// schedule as the source of truth for "scheduled days". Days with no explicit
// record count as assumed-present — matching the rest of the app (see statusOf
// in ListingsTab / AttendanceTab). Hours & delta are derived only from days that
// actually have check-in/out, so an unrecorded day doesn't skew the averages.
//
// `unrecordedDays` (2026-09-06) counts the scheduled PAST days that had no record at all and
// were therefore assumed present. The rate's definition is unchanged (app-wide rule); the count
// is reported beside it so a 100% cannot hide an agent who never checks in.
export function computeAgentStats(agent, records, lateThreshold, from, to, schedHist = [], opts = {}) {
  // The estimated-day fallback (owner ruling 2026-09-10). `activityDates` is the set of dates on
  // which this agent demonstrably did something; `officeDayMins(date)` answers "how long is a
  // working day here" from the office row. Both absent ⇒ this function behaves exactly as before,
  // which is why every existing caller and test needs no change.
  const activityDates = opts.activityDates || null
  const officeDayMins = typeof opts.officeDayMins === 'function' ? opts.officeDayMins : () => null
  const didSomethingOn = date => !!(activityDates && (activityDates.has ? activityDates.has(date) : activityDates[date] > 0))
  const traceDates = opts.traceDates instanceof Set ? opts.traceDates : null
  let remoteDays = 0
  const silentRemoteDates = [], silentOfficeDates = []
  const recByDate = {}
  records.forEach(rec => { if (rec?.date) recByDate[rec.date] = rec })
  const todayStr = today()

  let scheduledDays = 0, presentDays = 0, absentDays = 0, unrecordedDays = 0
  let workedDays = 0, totalWorkedMins = 0, totalScheduledMins = 0
  let lateDays = 0, onTimeDays = 0
  // Owner ruling 2026-09-10: "instead of დროულობა we need დაგვიანება (რამდენჯერ / რამდენი წუთი
  // ჯამში), გაცდენა (რამდენჯერ). Two variables that define attendance. We need to have also
  // overtime work." A RATE hides both halves an office actually acts on — five minutes late
  // twenty times and two hours late once are the same 0%, and the manager can do something
  // about only one of them. So the counts and the MINUTES are carried beside the rate.
  // ⚠️ `onTimeRate` is deliberately still computed: it feeds the reliability score
  // (`performance.js` reliabilityOf, 0.7·attendance + 0.3·onTime) and the trend charts. This
  // ruling was about what the TABLE shows; re-scoring is a separate decision.
  let lateMins = 0, overtimeMins = 0
  // Days credited from the office's standard hours because no clock pair exists for them.
  // Reported SEPARATELY from the measured total so the table can mark the figure as derived —
  // a house rule this codebase already keeps for property facts (the `≈` mark).
  let estimatedWorkedDays = 0, estimatedWorkedMins = 0

  let startStr = from || '2020-01-01'
  if (agent.hire_date && agent.hire_date > startStr) startStr = agent.hire_date
  const endStr = to && to < today() ? to : today()

  const cursor = new Date(startStr + 'T12:00:00')
  const end    = new Date(endStr + 'T12:00:00')
  for (; cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
    const date = toStr(cursor)
    if (agent.is_active === false && agent.resign_date && date >= agent.resign_date) continue

    const rec = recByDate[date]
    if (rec?.status === 'holiday') continue

    const slots      = slotsOnDate(agent, date, schedHist)
    const schedStart = slots[0]?.start
    const schedMins  = scheduledMinsFromSchedule(agent, date, schedHist)
    const isAbsent   = rec?.status === 'absent'
    if (schedMins === 0 && !isAbsent) {
      // 🔴 NOT NECESSARILY A DAY OFF. A manager's roster is often a formality — Irakli's says
      // Mondays and he published 466 adverts — so a day with no scheduled slot but real work on
      // it is a worked day the calendar simply does not know about. Credit the office's standard
      // hours for that weekday and count nothing else.
      //
      // ⚠️ It is credited to HOURS ONLY. It does not become a scheduled day, a present day, or an
      // absence, and it can never produce lateness or overtime — those are statements ABOUT the
      // clock, and there is no clock reading here to make them from. Inventing one would report a
      // late arrival, or an hour of unpaid overtime, that nobody observed.
      // ⚠️ THE MEASURED PAIR COMES FIRST, and leaving it out would have made clocking in COST
      // an agent hours: with only the estimate wired, an unscheduled day worked 09:00–18:00 on
      // the clock credited nothing while the same day left unclocked credited a full office day.
      // A real reading must never lose to a credited one.
      const offWorked = workedMinutesOf(rec, diffMinutes)
      if (offWorked > 0) {
        // Billed against ITSELF, so an unrostered day contributes 0 to delta and 0 to overtime.
        // "He worked nine hours on a day the roster does not mention" is a fact about hours; it
        // is not nine hours of overtime, because no expectation was set for that date.
        totalWorkedMins += offWorked; totalScheduledMins += offWorked; workedDays++
      } else if (didSomethingOn(date)) {
        const est = officeDayMins(date)
        if (est) {
          totalWorkedMins += est; totalScheduledMins += est; workedDays++
          estimatedWorkedDays++; estimatedWorkedMins += est
        }
      }
      continue
    }

    scheduledDays++
    if (isAbsent) { absentDays++; continue }
    presentDays++
    if (!rec && date < todayStr) unrecordedDays++
    // SILENT SCHEDULED DAYS (owner ruling 2026-09-29, the agent score's leak check). Given the
    // dates on which the agent left any trace in the system (`opts.traceDates`), list the scheduled,
    // non-absent, finished days with none — split by where the roster put them. Computed HERE so
    // "what is a scheduled day" keeps its one definition; nothing above changes when it is absent.
    if (traceDates && date < todayStr) {
      const remote = rec?.status === 'remote' || (slots.length > 0 && slots.every(s => s.location === 'home'))
      if (remote) remoteDays++
      if (!traceDates.has(date)) (remote ? silentRemoteDates : silentOfficeDates).push(date)
    }

    // The FIRST check-in — lateness only. See workedMinutesOf for why these differ.
    const checkIn = rec?.check_in || (Array.isArray(rec?.slots) && rec.slots[0]?.check_in)
    const worked = workedMinutesOf(rec, diffMinutes)
    // A SCHEDULED day he worked without completing a clock pair (checked in and never out, or
    // never touched the clock at all). The honest credit here is his OWN scheduled length, not
    // the office default — the roster does state an expectation for this date, and using it
    // makes the day contribute exactly zero to delta and zero to overtime.
    if (worked === 0 && didSomethingOn(date) && schedMins > 0) {
      totalWorkedMins += schedMins; totalScheduledMins += schedMins; workedDays++
      estimatedWorkedDays++; estimatedWorkedMins += schedMins
    }
    if (worked > 0) {
      totalWorkedMins += worked; totalScheduledMins += schedMins; workedDays++
      // ⚠️ OVERTIME IS THE SUM OF THE POSITIVE DAYS, NOT THE NET. `delta` below already carries
      // the net, and the two answer different questions: an agent who works three hours over on
      // Monday and three hours under on Friday has a net of zero and DID three hours of
      // overtime. Netting them would report "no overtime" to someone who stayed late, which is
      // the number they would query first. Both are returned; neither replaces the other.
      if (worked > schedMins) overtimeMins += worked - schedMins
    }

    if (schedStart && checkIn) {
      const sm = parseInt(schedStart.split(':')[0])*60 + parseInt(schedStart.split(':')[1])
      const am = parseInt(checkIn.split(':')[0])*60   + parseInt(checkIn.split(':')[1])
      // ⚠️ Minutes are counted from the SCHEDULED START, not from the threshold, and only on
      // days that cross it. "How many minutes late in total" is the question, so a 40-minute
      // arrival contributes 40 — not 40 minus the grace. The threshold decides WHETHER the day
      // is late; it is not a discount on the minutes once it is.
      if (am - sm > lateThreshold) { lateDays++; lateMins += am - sm } else onTimeDays++
    }
  }

  return {
    scheduledDays, presentDays, absentDays, unrecordedDays,
    attendanceRate: scheduledDays > 0 ? Math.round(presentDays / scheduledDays * 100) : null,
    onTimeRate: (onTimeDays + lateDays) > 0 ? Math.round(onTimeDays / (onTimeDays + lateDays) * 100) : null,
    lateDays, onTimeDays,
    // The owner's two attendance variables, plus overtime (2026-09-10). `lateDays`/`absentDays`
    // are the "how many times"; these are the minutes behind them.
    lateMins, overtimeMins,
    // How much of totalWorkedMins was credited rather than measured (0 = every minute is a real
    // clock reading). The table renders the hours with `≈` whenever this is non-zero and names
    // the day count, so a figure built partly on the office's standard hours can never be read
    // as a measurement.
    estimatedWorkedDays, estimatedWorkedMins,
    // Only when opts.traceDates was given (the agent score). Empty otherwise.
    remoteDays, silentRemoteDates, silentOfficeDates,
    totalWorkedMins, totalScheduledMins,
    delta: totalWorkedMins - totalScheduledMins,
    avgWorkedMins: workedDays > 0 ? Math.round(totalWorkedMins / workedDays) : 0,
  }
}

// ─── Trend chart data (adaptive buckets) ──────────────────────────────────────
// Short ranges bucket by day, medium by week, long by month — so a single month
// of data still shows a real trend instead of one lonely point.
export function pickGranularity(from, to) {
  const days = Math.round((new Date(to + 'T12:00:00') - new Date(from + 'T12:00:00')) / 86400000)
  if (days <= 45) return 'day'
  if (days <= 180) return 'week'
  return 'month'
}
export function weekStart(dateStr) {
  const d = new Date(dateStr + 'T12:00:00')
  const dow = (d.getDay() + 6) % 7   // Monday = 0
  d.setDate(d.getDate() - dow)
  return toStr(d)
}
export function bucketKey(dateStr, gran) {
  if (gran === 'day')  return dateStr
  if (gran === 'week') return weekStart(dateStr)
  return monthKey(dateStr)
}
export function bucketLabel(key, gran) {
  if (gran === 'month') return monthLabel(key)
  return new Date(key + 'T12:00:00').toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short' })
}

// Results over time, from the SAME ledger the cards read (§14) — gross commission, closed
// transactions and published listings per bucket. Buckets use the shared bucketKey/gran so the
// outcome chart and the discipline charts below it line up on the x-axis; two trend charts of
// the same period that disagree about where a week starts are worse than one.
//
// Published is summed from the per-agent activity byDate maps rather than re-counting events:
// computeActivity already decided what "published" means (confirmed posts, joined by the
// agent's own event keys) and this must not become a second definition.
export function buildResultSeries(transactions, rows, gran) {
  const buckets = {}
  const at = k => (buckets[k] ||= { key: k, commission: 0, deals: 0, published: 0 })
  for (const tx of transactions) {
    if (!tx.date) continue
    const b = at(bucketKey(tx.date, gran))
    b.commission += tx.gross
    b.deals += 1
  }
  for (const r of rows) {
    for (const [date, n] of Object.entries(r.activity?.byDate || {})) at(bucketKey(date, gran)).published += n
  }
  return Object.keys(buckets).sort().map(k => ({ ...buckets[k], label: bucketLabel(k, gran) }))
}

// ONE agent over time (spec §4). Deliberately not buildResultSeries with a filter: that one
// sums the deal's GROSS, which is the company's question. An agent's chart must show what the
// AGENT earned, so it reads their own share out of the same ledger transaction.
//
// Discipline here is computed from ATTENDANCE RECORDS ONLY — days that have a check-in — and
// the axis is labelled that way. The headline attendance rate elsewhere uses scheduled days,
// which include days assumed present with no record; that denominator cannot be reconstructed
// per bucket without re-walking the schedule, and quietly mixing the two would put two
// different "attendance %" on one screen. Punctuality is on/(on+late), the same definition
// the cards use, which IS reconstructable because both sides need a check-in.
export function buildAgentSeries(agentId, transactions, activityByDate, records, agent, lateThreshold, gran, schedHist = []) {
  const buckets = {}
  const at = k => (buckets[k] ||= { key: k, commission: 0, deals: 0, published: 0, onTime: 0, late: 0 })

  for (const tx of transactions) {
    const mine = tx.agents.find(x => x.agent_id === agentId)
    if (!mine || !tx.date) continue
    const b = at(bucketKey(tx.date, gran))
    b.commission += mine.commission
    b.deals += 1
  }
  for (const [date, n] of Object.entries(activityByDate || {})) at(bucketKey(date, gran)).published += n

  for (const rec of records || []) {
    if (!rec?.date || rec.status === 'holiday') continue
    const checkIn = rec.check_in || (Array.isArray(rec.slots) && rec.slots[0]?.check_in)
    const schedStart = slotsOnDate(agent, rec.date, schedHist)[0]?.start
    if (!checkIn || !schedStart) continue
    const b = at(bucketKey(rec.date, gran))
    const sm = parseInt(schedStart.split(':')[0]) * 60 + parseInt(schedStart.split(':')[1])
    const am = parseInt(checkIn.split(':')[0]) * 60 + parseInt(checkIn.split(':')[1])
    if (am - sm > lateThreshold) b.late++; else b.onTime++
  }

  return Object.keys(buckets).sort().map(k => {
    const b = buckets[k]
    const seen = b.onTime + b.late
    return { ...b, label: bucketLabel(k, gran), days: seen,
      // null, not 0, when the bucket has no clock-ins — recharts leaves a gap, where a 0
      // would draw a plunge to the floor and read as "nobody was ever on time that week".
      punctuality: seen ? Math.round(b.onTime / seen * 100) : null }
  })
}

export function buildSeries(activeAgents, records, lateThreshold, gran, schedHist = []) {
  const buckets = {}
  records.forEach(rec => {
    const k = bucketKey(rec.date, gran)
    if (!buckets[k]) buckets[k] = []
    buckets[k].push(rec)
  })

  return Object.keys(buckets).sort().map(k => {
    const recs = buckets[k]
    let scheduled = 0, present = 0, absent = 0, late = 0
    let officeDays = 0, remoteDays = 0, workedMins = 0

    activeAgents.forEach(agent => {
      const agentRecs = recs.filter(r => r.agent_id === agent.id)
      agentRecs.forEach(rec => {
        if (rec.status === 'holiday') return
        // Use the current-schedule-based estimate (works for PAST dates too).
        // The old snapshot-based scheduledMinutes() returned 0 for past days,
        // which silently dropped almost every record from these charts.
        const schedMins = scheduledMinsFromSchedule(agent, rec.date, schedHist)
        if (schedMins === 0 && rec.status !== 'absent') return

        scheduled++
        if (rec.status === 'absent') { absent++; return }
        present++

        const slots = slotsOnDate(agent, rec.date, schedHist)
        const hasOffice = slots.some(s => s.location === 'office')
        const hasHome   = slots.some(s => s.location === 'home')
        if (hasOffice) officeDays++
        else if (hasHome) remoteDays++

        const schedStart = slots[0]?.start
        const checkIn = rec.check_in || (Array.isArray(rec.slots) && rec.slots[0]?.check_in)
        if (schedStart && checkIn) {
          const sm = parseInt(schedStart.split(':')[0])*60 + parseInt(schedStart.split(':')[1])
          const am = parseInt(checkIn.split(':')[0])*60   + parseInt(checkIn.split(':')[1])
          if (am - sm > lateThreshold) late++
        }

        // Same defect as the per-agent block above, and it was NOT in the report that found
        // that one — fixed together, because two copies of a rule is how they drift.
        workedMins += workedMinutesOf(rec, diffMinutes)
      })
    })

    const attendance = scheduled > 0 ? Math.round(present / scheduled * 100) : 0
    const absentRate = scheduled > 0 ? +(absent / scheduled * 100).toFixed(1) : 0
    const lateRate   = present  > 0 ? Math.round(late / present * 100) : 0

    return { month: bucketLabel(k, gran), attendance, absentRate, lateRate, office: officeDays, remote: remoteDays, absent, present }
  })
}
